import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../hooks/useTheme";
import PlanFormModal from "./PlanFormModal";

const WHATSAPP_BASE =
	"https://wa.me/18495388899?text=Hola%20InnoFlex!%20Me%20gustar%C3%ADa%20el%20";

function AnimatedPrice({
	price,
	isLight,
}: {
	price: string;
	isLight: boolean;
}) {
	const [display, setDisplay] = useState(price);
	const [animating, setAnimating] = useState(false);
	const prev = useRef(price);

	useEffect(() => {
		if (prev.current !== price) {
			setAnimating(true);
			const timer = setTimeout(() => {
				setDisplay(price);
				setAnimating(false);
				prev.current = price;
			}, 180);
			return () => clearTimeout(timer);
		}
	}, [price]);

	return (
		<span
			className="font-heading text-3xl md:text-4xl font-bold leading-none inline-block"
			style={{
				color: isLight ? "#0a1628" : "#ffffff",
				opacity: animating ? 0 : 1,
				transform: animating ? "translateY(-8px)" : "translateY(0)",
				transition: "opacity 0.18s ease, transform 0.18s ease",
			}}
		>
			${display}
		</span>
	);
}

interface PlanDef {
	id: string;
	name: string;
	taglineKey: string;
	price: string;
	implPrice: string;
	badge: string | null;
	accentHex: string;
	limits: { icon: string; key: string; tooltipKey?: string }[];
	limitValues: {
		users: string;
		cajas: string;
		warehouses: string;
		invoices: string;
		branches: string;
		support: string;
	};
	features: { textKey: string; included: boolean }[];
	ctaKey: string;
	waText: string;
	priceOnRequest?: boolean;
}

interface CompareFeature {
	type?: "feature" | "limit-val" | "group";
	key: string;
	planIds?: string[];
	limitField?: keyof PlanDef["limitValues"];
}

export default function Pricing() {
	const { theme } = useTheme();
	const isLight = theme === "light";
	const { t } = useTranslation();
	const [annual, setAnnual] = useState(false);
	const [view, setView] = useState<"cards" | "compare">("cards");
	const [visible, setVisible] = useState(false);
	const [modalPlan, setModalPlan] = useState<{
		name: string;
		color: string;
	} | null>(null);
	const sectionRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) setVisible(true);
			},
			{ threshold: 0.1 },
		);
		if (sectionRef.current) observer.observe(sectionRef.current);
		return () => observer.disconnect();
	}, []);

	const getPrice = (base: string) =>
		annual ? (parseFloat(base) * 0.85).toFixed(2) : base;

	const handleSelectPlan = (plan: PlanDef, color: string) => {
		if (plan.id === "personalizado") {
			window.open(
				`${WHATSAPP_BASE}${plan.waText}`,
				"_blank",
				"noopener,noreferrer",
			);
			return;
		}
		setModalPlan({ name: plan.name, color });
	};

	const planDefs: PlanDef[] = [
		{
			id: "starter",
			name: "Starter",
			taglineKey: "plan_starter_tagline",
			price: "19.99",
			implPrice: "$200",
			badge: null,
			accentHex: "#0ea5e9",
			ctaKey: "plan_starter_cta",
			waText: "Plan%20Starter",
			limits: [
				{ icon: "ri-user-line", key: "plan_starter_l1" },
				{ icon: "ri-computer-line", key: "plan_starter_l2" },
				{ icon: "ri-store-line", key: "plan_starter_l3" },
				{ icon: "ri-file-list-line", key: "plan_starter_l4" },
				{ icon: "ri-building-line", key: "plan_starter_l5" },
				{ icon: "ri-mail-line", key: "plan_starter_l6" },
			],
			limitValues: {
				users: "1",
				cajas: "1",
				warehouses: "1",
				invoices: "e-CF separado",
				branches: "1",
				support: "Email",
			},
			features: [
				{ textKey: "pf_billing_basic", included: true },
				{ textKey: "pf_inventory", included: true },
				{ textKey: "pf_reports_basic", included: true },
				{ textKey: "pf_mobile", included: true },
				{ textKey: "pf_employees", included: true },
				{ textKey: "pf_repair_log", included: true },
				{ textKey: "pf_support_email", included: true },
			],
		},
		{
			id: "negocio",
			name: "Business",
			taglineKey: "plan_business_tagline",
			price: "39.99",
			implPrice: "$200",
			badge: "pricing_badge_popular",
			accentHex: "#2563eb",
			ctaKey: "plan_business_cta",
			waText: "Plan%20Business",
			limits: [
				{ icon: "ri-group-line", key: "plan_business_l1" },
				{ icon: "ri-computer-line", key: "plan_business_l2" },
				{ icon: "ri-store-2-line", key: "plan_business_l3" },
				{
					icon: "ri-file-list-3-line",
					key: "plan_business_l4",
					tooltipKey: "plan_invoice_tooltip",
				},
				{ icon: "ri-building-line", key: "plan_business_l5" },
				{ icon: "ri-customer-service-line", key: "plan_business_l6" },
			],
			limitValues: {
				users: "3",
				cajas: "3",
				warehouses: "5",
				invoices: "50/mes",
				branches: "1",
				support: "L-V 2-24h",
			},
			features: [
				{ textKey: "pf_includes_starter", included: true },
				{ textKey: "pf_multi_register", included: true },
				{ textKey: "pf_purchases", included: true },
				{ textKey: "pf_credits", included: true },
				{ textKey: "pf_appointments", included: false },
				{ textKey: "pf_multistore", included: false },
				{ textKey: "pf_support_business_detail", included: true },
			],
		},
		{
			id: "pro",
			name: "Pro",
			taglineKey: "plan_pro_tagline",
			price: "59.99",
			implPrice: "$200",
			badge: null,
			accentHex: "#10b981",
			ctaKey: "plan_pro_cta",
			waText: "Plan%20Pro",
			limits: [
				{ icon: "ri-group-line", key: "plan_pro_l1" },
				{ icon: "ri-computer-line", key: "plan_pro_l2" },
				{ icon: "ri-store-2-line", key: "plan_pro_l3" },
				{
					icon: "ri-file-text-line",
					key: "plan_pro_l4",
					tooltipKey: "plan_invoice_tooltip",
				},
				{ icon: "ri-building-line", key: "plan_pro_l5" },
				{ icon: "ri-headphone-line", key: "plan_pro_l6" },
			],
			limitValues: {
				users: "8",
				cajas: "5",
				warehouses: "10",
				invoices: "100/mes",
				branches: "2",
				support: "Prio. 1-4h",
			},
			features: [
				{ textKey: "pf_includes_business", included: true },
				{ textKey: "pf_appointments", included: true },
				{ textKey: "pf_whatsapp_bot", included: true },
				{ textKey: "pf_catalog", included: true },
				{ textKey: "pf_multistore", included: true },
				{ textKey: "pf_custom_funcs", included: false },
				{ textKey: "pf_support_pro_detail", included: true },
			],
		},
		{
			id: "premium",
			name: "Premium",
			taglineKey: "plan_premium_tagline",
			price: "89.99",
			implPrice: "$200",
			badge: "pricing_badge_max",
			accentHex: "#f4c542",
			ctaKey: "plan_premium_cta",
			waText: "Plan%20Premium",
			limits: [
				{ icon: "ri-team-line", key: "plan_premium_l1" },
				{ icon: "ri-computer-line", key: "plan_premium_l2" },
				{ icon: "ri-community-line", key: "plan_premium_l3" },
				{
					icon: "ri-file-text-line",
					key: "plan_premium_l4",
					tooltipKey: "plan_invoice_tooltip",
				},
				{ icon: "ri-building-4-line", key: "plan_premium_l5" },
				{ icon: "ri-customer-service-2-line", key: "plan_premium_l6" },
			],
			limitValues: {
				users: "15",
				cajas: "10",
				warehouses: "20",
				invoices: "200/mes",
				branches: "3",
				support: "Dedicado",
			},
			features: [
				{ textKey: "pf_includes_pro", included: true },
				{ textKey: "pf_custom_hours", included: true },
				{ textKey: "pf_custom_funcs", included: true },
				{ textKey: "pf_training", included: true },
				{ textKey: "pf_onboarding", included: true },
				{ textKey: "pf_custom_modules", included: false },
				{ textKey: "pf_support_premium_detail", included: true },
			],
		},
		{
			id: "personalizado",
			name: "Personalizado",
			taglineKey: "plan_custom_tagline",
			price: "0",
			implPrice: "",
			badge: "pricing_badge_custom",
			accentHex: "#a855f7",
			ctaKey: "plan_custom_cta",
			waText: "Plan%20Personalizado",
			priceOnRequest: true,
			limits: [
				{ icon: "ri-settings-3-line", key: "plan_custom_l1" },
				{ icon: "ri-computer-line", key: "plan_custom_l2" },
				{ icon: "ri-community-line", key: "plan_custom_l3" },
				{ icon: "ri-file-text-line", key: "plan_custom_l4" },
				{ icon: "ri-building-4-line", key: "plan_custom_l5" },
				{ icon: "ri-customer-service-2-line", key: "plan_custom_l6" },
			],
			limitValues: {
				users: "∞",
				cajas: "∞",
				warehouses: "∞",
				invoices: "A medida",
				branches: "∞",
				support: "SLA Dedicado",
			},
			features: [
				{ textKey: "pf_includes_premium", included: true },
				{ textKey: "pf_custom_modules", included: true },
				{ textKey: "pf_exclusive_features", included: true },
				{ textKey: "pf_early_access", included: true },
				{ textKey: "pf_support_custom_detail", included: true },
			],
		},
	];

	const allFeatureKeys: CompareFeature[] = [
		{ type: "group", key: "compare_group_limits" },
		{ type: "limit-val", key: "compare_lim_users", limitField: "users" },
		{ type: "limit-val", key: "compare_lim_cajas", limitField: "cajas" },
		{
			type: "limit-val",
			key: "compare_lim_warehouses",
			limitField: "warehouses",
		},
		{ type: "limit-val", key: "compare_lim_invoices", limitField: "invoices" },
		{ type: "limit-val", key: "compare_lim_branches", limitField: "branches" },
		{ type: "limit-val", key: "compare_lim_support", limitField: "support" },
		{
			key: "pf_billing_basic",
			planIds: ["starter", "negocio", "pro", "premium", "personalizado"],
		},
		{
			key: "pf_inventory",
			planIds: ["starter", "negocio", "pro", "premium", "personalizado"],
		},
		{
			key: "pf_repair_log",
			planIds: ["starter", "negocio", "pro", "premium", "personalizado"],
		},
		{ key: "pf_includes_starter", planIds: ["negocio"] },
		{ key: "pf_includes_business", planIds: ["pro"] },
		{ key: "pf_includes_pro", planIds: ["premium"] },
		{ key: "pf_includes_premium", planIds: ["personalizado"] },
		{
			key: "pf_multi_register",
			planIds: ["negocio", "pro", "premium", "personalizado"],
		},
		{
			key: "pf_reports_basic",
			planIds: ["starter", "negocio", "pro", "premium", "personalizado"],
		},
		{
			key: "pf_mobile",
			planIds: ["starter", "negocio", "pro", "premium", "personalizado"],
		},
		{
			key: "pf_employees",
			planIds: ["starter", "negocio", "pro", "premium", "personalizado"],
		},
		{
			key: "pf_purchases",
			planIds: ["negocio", "pro", "premium", "personalizado"],
		},
		{
			key: "pf_credits",
			planIds: ["negocio", "pro", "premium", "personalizado"],
		},
		{ key: "pf_appointments", planIds: ["pro", "premium", "personalizado"] },
		{ key: "pf_whatsapp_bot", planIds: ["pro", "premium", "personalizado"] },
		{ key: "pf_catalog", planIds: ["pro", "premium", "personalizado"] },
		{ key: "pf_multistore", planIds: ["pro", "premium", "personalizado"] },
		{ key: "pf_custom_hours", planIds: ["premium", "personalizado"] },
		{ key: "pf_custom_funcs", planIds: ["premium"] },
		{ key: "pf_custom_modules", planIds: ["personalizado"] },
		{ key: "pf_exclusive_features", planIds: ["personalizado"] },
		{ key: "pf_training", planIds: ["premium", "personalizado"] },
		{ key: "pf_onboarding", planIds: ["premium", "personalizado"] },
		{ key: "pf_support_email", planIds: ["starter"] },
		{ key: "pf_early_access", planIds: ["premium", "personalizado"] },
		{ key: "pf_support_starter_detail", planIds: ["starter"] },
		{ key: "pf_support_business_detail", planIds: ["negocio"] },
		{ key: "pf_support_pro_detail", planIds: ["pro"] },
		{ key: "pf_support_premium_detail", planIds: ["premium"] },
		{ key: "pf_support_custom_detail", planIds: ["personalizado"] },
	];

	const toggleBg = {
		backgroundColor: isLight ? "#e8eef8" : "rgba(12,22,48,0.8)",
		borderColor: isLight ? "#c5d4e8" : "rgba(255,255,255,0.10)",
	};

	const secondaryBadgeStyle = isLight
		? {
				backgroundColor: "rgba(208,160,24,0.12)",
				border: "1px solid rgba(208,160,24,0.34)",
				iconColor: "#b37a00",
				textColor: "#996700",
			}
		: {
				backgroundColor: "rgba(250,204,21,0.12)",
				border: "1px solid rgba(250,204,21,0.40)",
				iconColor: "#facc15",
				textColor: "#fde047",
			};

	return (
		<section
			id="pricing"
			className="bg-navy-950 py-14 md:py-24 px-5 md:px-6"
			ref={sectionRef}
		>
			<div className="max-w-7xl mx-auto">
				<div
					className="text-center mb-9 md:mb-12"
					style={{
						opacity: visible ? 1 : 0,
						transform: visible ? "translateY(0)" : "translateY(24px)",
						transition: "opacity 0.6s ease, transform 0.6s ease",
					}}
				>
					<span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">
						{t("pricing_label")}
					</span>
					<h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
						{t("pricing_title_1")}{" "}
						<span className="text-electric">{t("pricing_title_accent")}</span>
					</h2>
					<p className="font-body text-white/60 text-sm md:text-base max-w-xl mx-auto mb-6 md:mb-8">
						{t("pricing_sub")}
					</p>

					{/* Billing system clarification */}
					<div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-6 w-full px-2">
						<div className="flex items-center gap-2 bg-electric/10 border border-electric/25 rounded-full px-4 py-2 w-full sm:w-auto justify-center">
							<div className="w-4 h-4 flex items-center justify-center text-electric flex-shrink-0">
								<i className="ri-bill-line text-sm"></i>
							</div>
							<span className="font-body text-xs font-semibold text-electric text-center leading-snug">
								{t("pricing_billing_note")}
							</span>
						</div>
						<div
							className="flex items-center gap-2 rounded-full px-4 py-2 w-full sm:w-auto justify-center"
							style={{
								backgroundColor: secondaryBadgeStyle.backgroundColor,
								border: secondaryBadgeStyle.border,
							}}
						>
							<div
								className="w-4 h-4 flex items-center justify-center flex-shrink-0"
								style={{ color: secondaryBadgeStyle.iconColor }}
							>
								<i className="ri-sparkling-line text-sm"></i>
							</div>
							<span
								className="font-body text-xs font-semibold text-center leading-snug"
								style={{ color: secondaryBadgeStyle.textColor }}
							>
								{t("pricing_other_services")}
							</span>
						</div>
					</div>

					{/* Sticky controls row */}
					<div
						className="flex flex-col items-center gap-4 mb-8 w-full"
						style={{
							opacity: visible ? 1 : 0,
							transform: visible ? "translateY(0)" : "translateY(24px)",
							transition: "opacity 0.6s ease, transform 0.6s ease",
						}}
					>
						{/* Toggle row */}
						<div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto">
							{/* Monthly / Annual */}
							<div
								className="inline-flex items-center gap-1 rounded-full px-1 py-1 border w-full sm:w-auto justify-center"
								style={toggleBg}
							>
								<button
									onClick={() => setAnnual(false)}
									className={`flex-1 sm:flex-none px-4 py-2 rounded-full text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap touch-manipulation ${!annual ? "bg-electric text-navy-950" : ""}`}
									style={{
										color: annual
											? isLight
												? "rgba(10,22,40,0.50)"
												: "rgba(255,255,255,0.60)"
											: undefined,
									}}
								>
									{t("pricing_monthly")}
								</button>
								<button
									onClick={() => setAnnual(true)}
									className={`flex-1 sm:flex-none px-4 py-2 rounded-full text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 touch-manipulation ${annual ? "bg-electric text-navy-950" : ""}`}
									style={{
										color: !annual
											? isLight
												? "rgba(10,22,40,0.50)"
												: "rgba(255,255,255,0.60)"
											: undefined,
									}}
								>
									{t("pricing_annual")}
									<span
										className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${annual ? "bg-navy-950/20 text-navy-950" : "bg-[#00ff9d]/20 text-[#00ff9d]"}`}
									>
										−15%
									</span>
								</button>
							</div>

							{/* Cards / Compare */}
							<div
								className="inline-flex items-center gap-1 rounded-full px-1 py-1 border w-full sm:w-auto justify-center"
								style={toggleBg}
							>
								<button
									onClick={() => setView("cards")}
									className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap touch-manipulation"
									style={{
										backgroundColor:
											view === "cards"
												? isLight
													? "rgba(10,22,40,0.08)"
													: "rgba(255,255,255,0.10)"
												: "transparent",
										color:
											view === "cards"
												? isLight
													? "#0a1628"
													: "#ffffff"
												: isLight
													? "rgba(10,22,40,0.45)"
													: "rgba(255,255,255,0.50)",
									}}
								>
									<div className="w-4 h-4 flex items-center justify-center">
										<i className="ri-layout-grid-line text-xs"></i>
									</div>
									{t("pricing_cards")}
								</button>
								<button
									onClick={() => setView("compare")}
									className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap touch-manipulation"
									style={{
										backgroundColor:
											view === "compare"
												? isLight
													? "rgba(10,22,40,0.08)"
													: "rgba(255,255,255,0.10)"
												: "transparent",
										color:
											view === "compare"
												? isLight
													? "#0a1628"
													: "#ffffff"
												: isLight
													? "rgba(10,22,40,0.45)"
													: "rgba(255,255,255,0.50)",
									}}
								>
									<div className="w-4 h-4 flex items-center justify-center">
										<i className="ri-table-line text-xs"></i>
									</div>
									{t("pricing_compare")}
								</button>
							</div>
						</div>
					</div>
				</div>

				{view === "cards" && (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-6xl mx-auto">
						{planDefs.map((plan, i) => (
							<PlanCard
								key={plan.id}
								plan={plan}
								price={getPrice(plan.price)}
								annual={annual}
								isLight={isLight}
								index={i}
								visible={visible}
								t={t}
								onSelectPlan={(selectedPlan, color) =>
									handleSelectPlan(selectedPlan, color)
								}
							/>
						))}
					</div>
				)}

				{view === "compare" && (
					<CompareTable
						planDefs={planDefs}
						getPrice={getPrice}
						allFeatureKeys={allFeatureKeys}
						annual={annual}
						visible={visible}
						isLight={isLight}
						t={t}
						onSelectPlan={(selectedPlan, color) =>
							handleSelectPlan(selectedPlan, color)
						}
					/>
				)}

				<div
					className="mt-10 text-center"
					style={{
						opacity: visible ? 1 : 0,
						transition: "opacity 0.6s ease 0.8s",
					}}
				>
					<p className="font-body text-sm text-white/40">{t("pricing_note")}</p>
				</div>

				{/* FEL Addons Section */}
				<PricingAddons visible={visible} isLight={isLight} t={t} />
			</div>

			{modalPlan && (
				<PlanFormModal
					planName={modalPlan.name}
					planColor={modalPlan.color}
					onClose={() => setModalPlan(null)}
				/>
			)}
		</section>
	);
}

interface PlanCardProps {
	plan: {
		id: string;
		name: string;
		taglineKey: string;
		price: string;
		implPrice: string;
		badge: string | null;
		accentHex: string;
		limits: { icon: string; key: string; tooltipKey?: string }[];
		limitValues: {
			users: string;
			cajas: string;
			warehouses: string;
			invoices: string;
			branches: string;
			support: string;
		};
		features: { textKey: string; included: boolean }[];
		ctaKey: string;
		waText: string;
		priceOnRequest?: boolean;
	};
	price: string;
	annual: boolean;
	isLight: boolean;
	index: number;
	visible: boolean;
	t: (key: string) => string;
	onSelectPlan: (plan: PlanDef, color: string) => void;
}

function PlanCard({
	plan,
	price,
	annual,
	isLight,
	index,
	visible,
	t,
	onSelectPlan,
}: PlanCardProps) {
	const [hovered, setHovered] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const isPopular = plan.badge === "pricing_badge_popular";
	const delay = index * 0.1 + 0.2;
	const accentHex =
		isLight && plan.id === "premium" ? "#b9811f" : plan.accentHex;
	const includedFeatures = plan.features.filter((f) => f.included);
	const excludedFeatures = plan.features.filter((f) => !f.included);
	const compactFeatures = [
		...includedFeatures.slice(0, 5),
		...excludedFeatures.slice(0, 2),
	];
	const visibleFeatures = expanded ? plan.features : compactFeatures;
	const hiddenFeatureCount = Math.max(
		plan.features.length - compactFeatures.length,
		0,
	);

	const cardBg = isLight
		? isPopular
			? `linear-gradient(160deg, ${accentHex}18 0%, rgba(255,255,255,1) 100%)`
			: plan.id === "premium"
				? "linear-gradient(160deg, rgba(255,216,107,0.20) 0%, #fff8e8 40%, #ffffff 100%)"
				: "#ffffff"
		: isPopular
			? `linear-gradient(160deg, ${accentHex}26 0%, rgba(6,15,35,0.98) 100%)`
			: "rgba(12,22,48,0.95)";
	const cardBorder = isLight
		? hovered
			? accentHex + "cc"
			: accentHex + "60"
		: hovered
			? accentHex + "80"
			: accentHex + "30";
	const cardShadow = isLight
		? hovered
			? `0 0 32px ${accentHex}20, 0 8px 32px rgba(0,0,0,0.10)`
			: isPopular
				? `0 0 20px ${accentHex}15, 0 4px 16px rgba(0,0,0,0.06)`
				: "0 2px 12px rgba(0,0,0,0.06)"
		: hovered
			? `0 0 40px ${accentHex}25, 0 20px 60px rgba(0,0,0,0.4)`
			: isPopular
				? `0 0 30px ${accentHex}30`
				: "none";

	return (
		<div
			className="relative flex flex-col rounded-2xl border transition-all duration-300 cursor-default overflow-hidden"
			style={{
				borderColor: cardBorder,
				background: cardBg,
				boxShadow: cardShadow,
				transform: visible
					? hovered
						? isPopular
							? "translateY(-18px)"
							: "translateY(-10px)"
						: isPopular
							? "translateY(-8px)"
							: "translateY(0)"
					: "translateY(32px)",
				opacity: visible ? 1 : 0,
				transition: `opacity 0.55s ease ${delay}s, transform 0.35s cubic-bezier(0.34,1.56,0.64,1)`,
			}}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<div
				className="absolute top-0 left-0 right-0 h-[2px]"
				style={{
					background: `linear-gradient(to right, ${accentHex}, ${accentHex}40, transparent)`,
					opacity: hovered || isPopular ? 1 : 0.4,
				}}
			/>
			<div
				className="absolute -top-6 -right-6 w-32 h-32 rounded-full pointer-events-none"
				style={{
					background: `radial-gradient(circle, ${accentHex}18 0%, transparent 70%)`,
					opacity: hovered ? 1 : 0.3,
				}}
			/>

			<div className="flex flex-col flex-1 p-4 md:p-5 relative z-10">
				{plan.badge ? (
					<div
						className="self-start inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full font-body mb-4 border"
						style={{
							backgroundColor: accentHex + "18",
							borderColor: accentHex + "40",
							color: accentHex,
						}}
					>
						<div
							className="w-1.5 h-1.5 rounded-full animate-pulse"
							style={{ backgroundColor: accentHex }}
						/>
						{t(plan.badge)}
					</div>
				) : (
					<div className="h-9 mb-4" />
				)}

				<h3
					className="font-heading text-2xl font-extrabold mb-1"
					style={{
						color: accentHex,
						textShadow:
							hovered && !isLight ? `0 0 20px ${accentHex}60` : "none",
					}}
				>
					{plan.name}
				</h3>
				<p
					className="font-body text-xs mb-4 leading-snug"
					style={{
						color: isLight ? "rgba(10,22,40,0.55)" : "rgba(255,255,255,0.50)",
					}}
				>
					{t(plan.taglineKey)}
				</p>

				{plan.priceOnRequest ? (
					<div className="mb-1">
						<span
							className="font-heading text-3xl md:text-4xl font-bold leading-none"
							style={{ color: accentHex }}
						>
							{t("plan_custom_price")}
						</span>
					</div>
				) : (
					<>
						<div className="mb-1">
							<div className="flex items-end gap-1">
								<AnimatedPrice price={price} isLight={isLight} />
								<span
									className="font-body text-xs mb-1.5"
									style={{
										color: isLight
											? "rgba(10,22,40,0.50)"
											: "rgba(255,255,255,0.50)",
									}}
								>
									{t("pricing_usd_month")}
								</span>
							</div>
						</div>
						<div className="flex items-center gap-1.5 mb-4">
							<div
								className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0"
								style={{ color: accentHex }}
							>
								<i className="ri-add-circle-line text-xs"></i>
							</div>
							<span
								className="font-body text-xs"
								style={{
									color: isLight
										? "rgba(10,22,40,0.50)"
										: "rgba(255,255,255,0.45)",
								}}
							>
								<strong
									style={{
										color: isLight
											? "rgba(10,22,40,0.75)"
											: "rgba(255,255,255,0.70)",
									}}
								>
									{plan.implPrice}
								</strong>{" "}
								{t("pricing_impl")}
							</span>
						</div>
					</>
				)}

				{annual && !plan.priceOnRequest && (
					<div className="inline-flex items-center gap-1 bg-[#00ff9d]/10 border border-[#00ff9d]/20 text-[#00ff9d] text-xs font-semibold px-2.5 py-1 rounded-full font-body mb-3">
						<div className="w-3 h-3 flex items-center justify-center">
							<i className="ri-price-tag-3-line text-xs"></i>
						</div>
						{t("pricing_save")} $
						{(parseFloat(plan.price) * 0.15 * 12).toFixed(0)} USD/
						{t("pricing_annual").toLowerCase()}
					</div>
				)}

				<div className="grid grid-cols-3 gap-2 mb-4">
					<div
						className="rounded-lg px-2.5 py-2 text-center"
						style={{
							backgroundColor: accentHex + "10",
							border: `1px solid ${accentHex}20`,
						}}
					>
						<p
							className="font-body text-[10px] leading-none mb-1"
							style={{
								color: isLight
									? "rgba(10,22,40,0.45)"
									: "rgba(255,255,255,0.45)",
							}}
						>
							{t("compare_lim_users")}
						</p>
						<p
							className="font-body text-xs font-bold leading-none"
							style={{ color: isLight ? "#0a1628" : "#ffffff" }}
						>
							{plan.limitValues.users}
						</p>
					</div>
					<div
						className="rounded-lg px-2.5 py-2 text-center"
						style={{
							backgroundColor: accentHex + "10",
							border: `1px solid ${accentHex}20`,
						}}
					>
						<p
							className="font-body text-[10px] leading-none mb-1"
							style={{
								color: isLight
									? "rgba(10,22,40,0.45)"
									: "rgba(255,255,255,0.45)",
							}}
						>
							{t("compare_lim_invoices")}
						</p>
						<p
							className="font-body text-xs font-bold leading-none"
							style={{ color: isLight ? "#0a1628" : "#ffffff" }}
						>
							{plan.limitValues.invoices}
						</p>
					</div>
					<div
						className="rounded-lg px-2.5 py-2 text-center"
						style={{
							backgroundColor: accentHex + "10",
							border: `1px solid ${accentHex}20`,
						}}
					>
						<p
							className="font-body text-[10px] leading-none mb-1"
							style={{
								color: isLight
									? "rgba(10,22,40,0.45)"
									: "rgba(255,255,255,0.45)",
							}}
						>
							{t("compare_lim_support")}
						</p>
						<p
							className="font-body text-xs font-bold leading-none truncate"
							style={{ color: isLight ? "#0a1628" : "#ffffff" }}
							title={plan.limitValues.support}
						>
							{plan.limitValues.support}
						</p>
					</div>
				</div>

				{expanded && (
					<div className="grid grid-cols-2 gap-1.5 mb-4">
						{plan.limits.map((limit) => (
							<div
								key={limit.key}
								className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
								style={{
									backgroundColor: accentHex + "10",
									border: `1px solid ${accentHex}20`,
								}}
								title={limit.tooltipKey ? t(limit.tooltipKey) : undefined}
							>
								<div
									className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0"
									style={{ color: accentHex }}
								>
									<i className={`${limit.icon} text-xs`}></i>
								</div>
								<span
									className="font-body text-[11px] font-semibold leading-tight"
									style={{ color: isLight ? "#0a1628" : "#ffffff" }}
								>
									{t(limit.key)}
								</span>
							</div>
						))}
					</div>
				)}

				<div
					className="border-t mb-5"
					style={{
						borderColor: isLight
							? "rgba(10,22,40,0.08)"
							: "rgba(255,255,255,0.08)",
					}}
				/>

				<ul className="space-y-2 flex-1 mb-4">
					{visibleFeatures.map((f, fi) => (
						<li
							key={f.textKey}
							className="flex items-start gap-2 text-xs font-body"
							style={{
								color: f.included
									? isLight
										? "rgba(10,22,40,0.80)"
										: "rgba(255,255,255,0.80)"
									: isLight
										? "rgba(10,22,40,0.30)"
										: "rgba(255,255,255,0.28)",
								opacity: visible ? 1 : 0,
								transform: visible ? "translateX(0)" : "translateX(-8px)",
								transition: `opacity 0.4s ease ${delay + 0.05 * fi}s, transform 0.4s ease ${delay + 0.05 * fi}s`,
							}}
						>
							<div
								className="flex-shrink-0 mt-[1px]"
								style={{
									color: f.included
										? accentHex
										: isLight
											? "rgba(10,22,40,0.22)"
											: "rgba(255,255,255,0.20)",
									fontSize: "14px",
									lineHeight: 1,
								}}
							>
								<i
									className={
										f.included
											? "ri-checkbox-circle-fill"
											: "ri-checkbox-blank-circle-line"
									}
								/>
							</div>
							<span style={{ lineHeight: "1.45" }}>{t(f.textKey)}</span>
						</li>
					))}
				</ul>

				{(hiddenFeatureCount > 0 || expanded) && (
					<button
						onClick={() => setExpanded((prev) => !prev)}
						className="w-full mb-4 flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-semibold font-body cursor-pointer transition-all duration-200"
						style={{
							borderColor: accentHex + "35",
							color: accentHex,
							backgroundColor: accentHex + "10",
						}}
					>
						<i
							className={`text-sm ${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
						></i>
						{expanded
							? t("pricing_view_less")
							: `${t("pricing_view_more")} (+${hiddenFeatureCount})`}
					</button>
				)}

				<button
					onClick={() => onSelectPlan(plan, accentHex)}
					className={`w-full flex items-center justify-center gap-2 font-bold text-sm py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap font-body transform hover:scale-[1.03] active:scale-[0.97] hover:shadow-lg ${plan.id === "negocio" ? "bg-gradient-to-r from-[#2563eb] to-[#1d4ed8] text-white" : plan.id === "premium" ? "bg-gradient-to-r from-[#f8db8a] to-[#f4c542] text-navy-950" : plan.id === "pro" ? "bg-gradient-to-r from-[#10b981] to-[#059669] text-white" : plan.id === "personalizado" ? "bg-gradient-to-r from-[#8b5cf6] to-[#7c3aed] text-white" : "bg-gradient-to-r from-[#0ea5e9] to-[#0284c7] text-white"}`}
				>
					<div className="w-4 h-4 flex items-center justify-center">
						<i className="ri-arrow-right-line text-sm"></i>
					</div>
					{t(plan.ctaKey)}
				</button>
			</div>
		</div>
	);
}

interface CompareTableProps {
	planDefs: PlanDef[];
	getPrice: (base: string) => string;
	allFeatureKeys: CompareFeature[];
	annual: boolean;
	visible: boolean;
	isLight: boolean;
	t: (key: string) => string;
	onSelectPlan: (plan: PlanDef, color: string) => void;
}

function CompareTable({
	planDefs,
	getPrice,
	allFeatureKeys,
	annual,
	visible,
	isLight,
	t,
	onSelectPlan,
}: CompareTableProps) {
	const [hoveredCol, setHoveredCol] = useState<string | null>(null);
	const getPlanAccent = (plan: PlanDef) =>
		isLight && plan.id === "premium" ? "#b9811f" : plan.accentHex;

	const tableBorder = isLight
		? "rgba(10,22,40,0.12)"
		: "rgba(255,255,255,0.08)";
	const rowBorder = isLight ? "rgba(10,22,40,0.08)" : "rgba(255,255,255,0.05)";
	const headerBg = isLight ? "#edf2fa" : "rgba(12,22,48,0.9)";
	const featureCellBg = isLight ? "#f5f8ff" : "rgba(12,22,48,0.4)";
	const featureTextColor = isLight
		? "rgba(10,22,40,0.65)"
		: "rgba(255,255,255,0.60)";
	const headerLabelColor = isLight
		? "rgba(10,22,40,0.45)"
		: "rgba(255,255,255,0.40)";
	const priceColor = isLight ? "#0a1628" : "#ffffff";
	const priceMutedColor = isLight
		? "rgba(10,22,40,0.40)"
		: "rgba(255,255,255,0.40)";

	return (
		<div
			className="overflow-x-auto rounded-2xl border"
			style={{
				borderColor: tableBorder,
				opacity: visible ? 1 : 0,
				transform: visible ? "translateY(0)" : "translateY(20px)",
				transition: "opacity 0.5s ease 0.2s, transform 0.5s ease 0.2s",
			}}
		>
			<table
				className="w-full min-w-[950px]"
				style={{ borderCollapse: "collapse" }}
			>
				<thead>
					<tr>
						<th
							className="text-left p-5 font-body text-xs font-semibold uppercase tracking-wider w-56"
							style={{ backgroundColor: headerBg, color: headerLabelColor }}
						>
							{t("compare_feature_col")}
						</th>
						{planDefs.map((plan) => (
							<th
								key={plan.id}
								className="p-5 text-center cursor-pointer transition-all duration-200"
								style={{
									backgroundColor:
										hoveredCol === plan.id
											? getPlanAccent(plan) + (isLight ? "18" : "12")
											: isLight
												? "#f8fbff"
												: "rgba(12,22,48,0.95)",
									borderLeft: `1px solid ${rowBorder}`,
									minWidth: "130px",
								}}
								onMouseEnter={() => setHoveredCol(plan.id)}
								onMouseLeave={() => setHoveredCol(null)}
							>
								{plan.badge && (
									<div
										className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full font-body mb-2"
										style={{
											backgroundColor: getPlanAccent(plan) + "18",
											color: getPlanAccent(plan),
											border: `1px solid ${getPlanAccent(plan)}40`,
										}}
									>
										<div
											className="w-1.5 h-1.5 rounded-full animate-pulse"
											style={{ backgroundColor: getPlanAccent(plan) }}
										/>
										{t(plan.badge)}
									</div>
								)}
								{!plan.badge && <div className="h-5 mb-2" />}
								<div
									className="font-heading text-base font-extrabold"
									style={{ color: getPlanAccent(plan) }}
								>
									{plan.name}
								</div>
								{plan.priceOnRequest ? (
									<div
										className="font-heading text-base font-bold mt-1"
										style={{ color: getPlanAccent(plan) }}
									>
										{t("plan_custom_price")}
									</div>
								) : (
									<>
										<div
											className="font-heading text-xl font-bold mt-1"
											style={{ color: priceColor }}
										>
											${getPrice(plan.price)}
											<span
												className="text-xs font-body font-normal"
												style={{ color: priceMutedColor }}
											>
												/{t("pricing_monthly").toLowerCase()}
											</span>
										</div>
										{annual && (
											<div className="text-[10px] text-[#00ff9d] font-semibold font-body mt-1">
												−15% {t("pricing_annual").toLowerCase()}
											</div>
										)}
									</>
								)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{allFeatureKeys.map((feat, i) => {
						// ── Group header row ──────────────────────────────────────
						if (feat.type === "group") {
							return (
								<tr
									key={feat.key}
									style={{ borderTop: `1px solid ${rowBorder}` }}
								>
									<td
										colSpan={planDefs.length + 1}
										className="px-5 py-2.5 font-body text-[10px] font-bold uppercase tracking-widest"
										style={{
											backgroundColor: isLight
												? "#edf2fa"
												: "rgba(12,22,48,0.85)",
											color: isLight
												? "rgba(10,22,40,0.45)"
												: "rgba(255,255,255,0.35)",
										}}
									>
										{t(feat.key)}
									</td>
								</tr>
							);
						}
						// ── Limit value row ───────────────────────────────────────
						if (feat.type === "limit-val" && feat.limitField) {
							return (
								<tr
									key={feat.key}
									style={{ borderTop: `1px solid ${rowBorder}` }}
								>
									<td
										className="p-4 pl-5 font-body text-xs font-semibold"
										style={{
											backgroundColor: featureCellBg,
											color: featureTextColor,
										}}
									>
										{t(feat.key)}
									</td>
									{planDefs.map((plan) => {
										const val = plan.limitValues[feat.limitField!];
										const isUnlimited = val === "∞";
										return (
											<td
												key={plan.id}
												className="p-4 text-center transition-all duration-200"
												style={{
													backgroundColor:
														hoveredCol === plan.id
															? getPlanAccent(plan) + (isLight ? "10" : "08")
															: i % 2 === 0
																? isLight
																	? "rgba(10,22,40,0.01)"
																	: "rgba(255,255,255,0.01)"
																: "transparent",
													borderLeft: `1px solid ${rowBorder}`,
												}}
												onMouseEnter={() => setHoveredCol(plan.id)}
												onMouseLeave={() => setHoveredCol(null)}
											>
												<span
													className="font-body text-xs font-bold px-2 py-0.5 rounded-full inline-block"
													style={{
														backgroundColor:
															val === "—"
																? "transparent"
																: getPlanAccent(plan) + "15",
														color:
															val === "—"
																? isLight
																	? "rgba(10,22,40,0.25)"
																	: "rgba(255,255,255,0.25)"
																: isUnlimited
																	? getPlanAccent(plan)
																	: isLight
																		? "#0a1628"
																		: "#ffffff",
													}}
												>
													{val}
												</span>
											</td>
										);
									})}
								</tr>
							);
						}
						// ── Feature check row ─────────────────────────────────────
						return (
							<tr
								key={feat.key}
								style={{ borderTop: `1px solid ${rowBorder}` }}
							>
								<td
									className="p-4 pl-5 font-body text-xs"
									style={{
										backgroundColor: featureCellBg,
										color: featureTextColor,
									}}
								>
									{t(feat.key)}
								</td>
								{planDefs.map((plan) => {
									const has = feat.planIds?.includes(plan.id) ?? false;
									return (
										<td
											key={plan.id}
											className="p-4 text-center transition-all duration-200"
											style={{
												backgroundColor:
													hoveredCol === plan.id
														? getPlanAccent(plan) + (isLight ? "10" : "08")
														: i % 2 === 0
															? isLight
																? "rgba(10,22,40,0.01)"
																: "rgba(255,255,255,0.01)"
															: "transparent",
												borderLeft: `1px solid ${rowBorder}`,
											}}
											onMouseEnter={() => setHoveredCol(plan.id)}
											onMouseLeave={() => setHoveredCol(null)}
										>
											{has ? (
												<div className="flex justify-center">
													<div
														className="w-5 h-5 flex items-center justify-center rounded-full"
														style={{
															backgroundColor: getPlanAccent(plan) + "20",
															color: getPlanAccent(plan),
														}}
													>
														<i className="ri-check-line text-xs font-bold"></i>
													</div>
												</div>
											) : (
												<div className="flex justify-center">
													<div
														className="w-5 h-5 flex items-center justify-center"
														style={{
															color: isLight
																? "rgba(10,22,40,0.18)"
																: "rgba(255,255,255,0.15)",
														}}
													>
														<i className="ri-close-line text-xs"></i>
													</div>
												</div>
											)}
										</td>
									);
								})}
							</tr>
						);
					})}
					<tr
						style={{
							borderTop: `1px solid ${isLight ? "rgba(10,22,40,0.10)" : "rgba(255,255,255,0.08)"}`,
						}}
					>
						<td className="p-5" style={{ backgroundColor: featureCellBg }} />
						{planDefs.map((plan) => (
							<td
								key={plan.id}
								className="p-4 text-center transition-all duration-200"
								style={{
									backgroundColor:
										hoveredCol === plan.id
											? getPlanAccent(plan) + (isLight ? "12" : "10")
											: "transparent",
									borderLeft: `1px solid ${rowBorder}`,
								}}
								onMouseEnter={() => setHoveredCol(plan.id)}
								onMouseLeave={() => setHoveredCol(null)}
							>
								<button
									onClick={() => onSelectPlan(plan, getPlanAccent(plan))}
									className="inline-flex items-center justify-center gap-1.5 font-bold text-xs py-2.5 px-4 rounded-xl font-body transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-[1.04] active:scale-[0.97]"
									style={{
										background: `linear-gradient(to right, ${getPlanAccent(plan)}, ${getPlanAccent(plan)}cc)`,
										color: "#060f23",
									}}
								>
									<i className="ri-arrow-right-line text-xs"></i>
									{t(plan.ctaKey)}
								</button>
							</td>
						))}
					</tr>
				</tbody>
			</table>
		</div>
	);
}

// ─── FEL Addons ───────────────────────────────────────────────────────────────
interface PricingAddonsProps {
	visible: boolean;
	isLight: boolean;
	t: (key: string) => string;
}

function PricingAddons({ visible, isLight, t }: PricingAddonsProps) {
	const addons = [
		{
			id: "impl",
			titleKey: "pricing_addon1_title",
			descKey: "pricing_addon1_desc",
			priceKey: "pricing_addon1_price",
			badgeKey: "pricing_addon1_badge",
			savingsKey: null,
			accentHex: "#00cfff",
			icon: "ri-settings-3-line",
			highlight: false,
		},
		{
			id: "fel",
			titleKey: "pricing_addon2_title",
			descKey: "pricing_addon2_desc",
			priceKey: "pricing_addon2_price",
			badgeKey: "pricing_addon2_badge",
			savingsKey: null,
			accentHex: "#e53535",
			icon: "ri-file-text-line",
			highlight: false,
		},
		{
			id: "bundle",
			titleKey: "pricing_addon3_title",
			descKey: "pricing_addon3_desc",
			priceKey: "pricing_addon3_price",
			badgeKey: "pricing_addon3_badge",
			savingsKey: "pricing_addon3_savings",
			accentHex: "#ffd86b",
			icon: "ri-sparkling-2-line",
			highlight: true,
		},
	];

	const WHATSAPP_BUNDLE =
		"https://wa.me/18495388899?text=Hola%20InnoFlex!%20Me%20gustar%C3%ADa%20el%20Paquete%20Completo%20(Implementaci%C3%B3n%20%2B%20e-CF%20%2B%20Portal%20RepublicaFEL)";

	const getAddonAccent = (addon: (typeof addons)[number]) =>
		isLight && addon.id === "bundle" ? "#c78d18" : addon.accentHex;

	const cardBg = (accent: string, highlight: boolean, addonId: string) =>
		isLight
			? highlight
				? addonId === "bundle"
					? "linear-gradient(135deg, rgba(255,216,107,0.22) 0%, #fff8e8 52%, #ffffff 100%)"
					: `linear-gradient(135deg, ${accent}12 0%, #ffffff 60%)`
				: "#ffffff"
			: highlight
				? `linear-gradient(135deg, ${accent}14 0%, rgba(6,15,35,0.97) 70%)`
				: "rgba(12,22,48,0.95)";

	return (
		<div
			className="mt-16"
			style={{
				opacity: visible ? 1 : 0,
				transform: visible ? "translateY(0)" : "translateY(24px)",
				transition: "opacity 0.6s ease 1s, transform 0.6s ease 1s",
			}}
		>
			{/* Divider */}
			<div className="flex items-center gap-4 mb-8">
				<div
					className="flex-1 h-px"
					style={{
						background: isLight
							? "rgba(10,22,40,0.10)"
							: "rgba(255,255,255,0.08)",
					}}
				/>
				<div
					className="flex items-center gap-2 px-4 py-1.5 rounded-full border"
					style={{
						borderColor: isLight
							? "rgba(10,22,40,0.14)"
							: "rgba(255,255,255,0.12)",
						backgroundColor: isLight ? "#f5f8ff" : "rgba(12,22,48,0.8)",
					}}
				>
					<div
						className="w-4 h-4 flex items-center justify-center"
						style={{ color: "#e53535" }}
					>
						<i className="ri-add-circle-line text-sm"></i>
					</div>
					<span
						className="font-body text-xs font-semibold tracking-widest uppercase"
						style={{
							color: isLight ? "rgba(10,22,40,0.55)" : "rgba(255,255,255,0.50)",
						}}
					>
						{t("pricing_addons_title")}
					</span>
				</div>
				<div
					className="flex-1 h-px"
					style={{
						background: isLight
							? "rgba(10,22,40,0.10)"
							: "rgba(255,255,255,0.08)",
					}}
				/>
			</div>

			<p
				className="text-center font-body text-sm mb-8"
				style={{
					color: isLight ? "rgba(10,22,40,0.55)" : "rgba(255,255,255,0.50)",
				}}
			>
				{t("pricing_addons_sub")}
			</p>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-5">
				{addons.map((addon) => {
					const accentHex = getAddonAccent(addon);
					return (
						<div
							key={addon.id}
							className="relative rounded-2xl border flex flex-col overflow-hidden"
							style={{
								borderColor: addon.highlight
									? accentHex + "60"
									: isLight
										? accentHex + "40"
										: accentHex + "28",
								background: cardBg(accentHex, addon.highlight, addon.id),
							}}
						>
							{/* Top accent line */}
							<div
								className="h-[2px] w-full"
								style={{
									background: `linear-gradient(to right, ${accentHex}, ${accentHex}00)`,
								}}
							/>

							<div className="p-6 flex flex-col flex-1">
								{/* Badge */}
								<div className="flex items-center justify-between mb-4">
									<span
										className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full font-body"
										style={{
											backgroundColor: accentHex + "18",
											color: accentHex,
											border: `1px solid ${accentHex}40`,
										}}
									>
										{addon.highlight && (
											<div
												className="w-1.5 h-1.5 rounded-full animate-pulse"
												style={{ backgroundColor: accentHex }}
											/>
										)}
										{t(addon.badgeKey)}
									</span>
									<div
										className="w-9 h-9 flex items-center justify-center rounded-xl"
										style={{
											backgroundColor: accentHex + "18",
											color: accentHex,
										}}
									>
										<i className={`${addon.icon} text-base`}></i>
									</div>
								</div>

								{/* Title & Desc */}
								<h4
									className="font-heading text-lg font-bold mb-2"
									style={{
										color: addon.highlight
											? accentHex
											: isLight
												? "#0a1628"
												: "#ffffff",
									}}
								>
									{t(addon.titleKey)}
								</h4>
								<p
									className="font-body text-xs leading-relaxed flex-1"
									style={{
										color: isLight
											? "rgba(10,22,40,0.60)"
											: "rgba(255,255,255,0.55)",
									}}
								>
									{t(addon.descKey)}
								</p>

								{/* RepublicaFEL badge on FEL addons */}
								{(addon.id === "fel" || addon.id === "bundle") && (
									<div className="flex items-center gap-2 mt-3 mb-2">
										<div
											className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
											style={{
												backgroundColor: "#e5353518",
												border: "1px solid #e5353540",
											}}
										>
											<div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
												<i
													className="ri-shield-check-line text-xs"
													style={{ color: "#e53535" }}
												></i>
											</div>
											<span
												className="font-body text-[10px] font-bold"
												style={{ color: "#e53535" }}
											>
												Powered by RepublicaFEL
											</span>
										</div>
									</div>
								)}

								{/* Savings tag */}
								{addon.savingsKey && (
									<div className="inline-flex items-center gap-1 mt-2 mb-3">
										<div
											className="w-3 h-3 flex items-center justify-center flex-shrink-0"
											style={{ color: accentHex }}
										>
											<i className="ri-price-tag-3-line text-xs"></i>
										</div>
										<span
											className="font-body text-xs font-semibold"
											style={{ color: accentHex }}
										>
											{t(addon.savingsKey)}
										</span>
									</div>
								)}

								{/* Price */}
								<div
									className="mt-4 pt-4 border-t flex items-end justify-between"
									style={{
										borderColor: isLight
											? "rgba(10,22,40,0.08)"
											: "rgba(255,255,255,0.08)",
									}}
								>
									<div>
										<span
											className="font-heading text-2xl font-extrabold"
											style={{ color: accentHex }}
										>
											{t(addon.priceKey)}
										</span>
										<span
											className="font-body text-xs ml-1"
											style={{
												color: isLight
													? "rgba(10,22,40,0.45)"
													: "rgba(255,255,255,0.40)",
											}}
										>
											USD · único pago
										</span>
									</div>
									{addon.id === "bundle" && (
										<a
											href={WHATSAPP_BUNDLE}
											target="_blank"
											rel="nofollow noopener noreferrer"
											className="inline-flex items-center gap-1.5 font-bold text-xs py-2 px-3.5 rounded-xl font-body transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-[1.04] active:scale-[0.97]"
											style={{
												background: `linear-gradient(to right, ${accentHex}, ${accentHex}cc)`,
												color: "#060f23",
											}}
										>
											<div className="w-3.5 h-3.5 flex items-center justify-center">
												<i className="ri-whatsapp-line text-xs"></i>
											</div>
											{t("partners_bundle_cta")}
										</a>
									)}
								</div>
							</div>
						</div>
					);
				})}
			</div>

			<p
				className="text-center font-body text-xs mt-6"
				style={{
					color: isLight ? "rgba(10,22,40,0.38)" : "rgba(255,255,255,0.30)",
				}}
			>
				{t("pricing_addons_note")}
			</p>
		</div>
	);
}
